import React, { useState } from 'react';
import { X, User as UserIcon, Lock, Phone, IdCard, LogIn, UserPlus } from 'lucide-react';
import { api, setCsrfToken } from '../api';
import { User } from '../types';

interface AuthModalProps {
  onClose: () => void;
  onLogin: (user: User) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, onLogin }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [idCard, setIdCard] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (mode === 'register' && password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    setLoading(true);
    try {
      const data = mode === 'login'
        ? await api<{ csrf_token: string } & User>('login', { phone, password }, 'POST')
        : await api<{ csrf_token: string } & User>('register', { name, phone, idCard, password }, 'POST');
      if (data.csrf_token) setCsrfToken(data.csrf_token);
      onLogin(data as User);
    } catch (ex: any) {
      setError(ex.message || '操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100">
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-sky-600 to-blue-600 text-white flex items-center justify-center shadow-lg shadow-sky-500/20">
            <UserIcon className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-slate-900">
              {mode === 'login' ? '欢迎回来' : '注册新账号'}
            </h3>
            <p className="text-xs text-slate-500">长顺县游泳协会 · 会员服务平台</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-slate-100 p-1 rounded-xl text-xs font-bold mb-6">
          <button
            onClick={() => { setMode('login'); setError(''); }}
            className={`flex-1 py-2 rounded-lg transition-all ${mode === 'login' ? 'bg-white text-sky-700 shadow' : 'text-slate-600'}`}
          >
            <LogIn className="w-3.5 h-3.5 inline mr-1" />账号登录
          </button>
          <button
            onClick={() => { setMode('register'); setError(''); }}
            className={`flex-1 py-2 rounded-lg transition-all ${mode === 'register' ? 'bg-white text-sky-700 shadow' : 'text-slate-600'}`}
          >
            <UserPlus className="w-3.5 h-3.5 inline mr-1" />注册账号
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">真实姓名 *</label>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-sky-500">
                <UserIcon className="w-4 h-4 text-slate-400 shrink-0" />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="与身份证一致" required className="w-full bg-transparent focus:outline-none text-sm" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">手机号 *</label>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-sky-500">
              <Phone className="w-4 h-4 text-slate-400 shrink-0" />
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="11位手机号" required maxLength={11} className="w-full bg-transparent focus:outline-none text-sm" />
            </div>
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">身份证号 *</label>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-sky-500">
                <IdCard className="w-4 h-4 text-slate-400 shrink-0" />
                <input type="text" value={idCard} onChange={(e) => setIdCard(e.target.value)} placeholder="18位身份证号" required maxLength={18} className="w-full bg-transparent focus:outline-none text-sm font-mono" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">密码 *</label>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-sky-500">
              <Lock className="w-4 h-4 text-slate-400 shrink-0" />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === 'register' ? '6位以上密码' : '请输入密码'} required minLength={6} className="w-full bg-transparent focus:outline-none text-sm" />
            </div>
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">确认密码 *</label>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-sky-500">
                <Lock className="w-4 h-4 text-slate-400 shrink-0" />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="再次输入密码" required minLength={6} className="w-full bg-transparent focus:outline-none text-sm" />
              </div>
            </div>
          )}

          {error && <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-500 hover:to-blue-500 text-white font-extrabold text-sm rounded-xl shadow-lg shadow-sky-600/25 disabled:opacity-60"
          >
            {loading ? '处理中...' : mode === 'login' ? '登录' : '注册并登录'}
          </button>
        </form>

        <p className="text-[11px] text-slate-400 text-center mt-4">
          注册即表示同意《长顺县游泳协会会员服务协议》与《参赛健康与安全声明》
        </p>
      </div>
    </div>
  );
};
